import React, { useMemo } from 'react';
import { eCommentType, prettyCommentsType } from 'types/order/comment/eCommentType';
import { internalCommentTypes } from 'types/order/comment/internalCommentTypes';
import { CommentLayout } from './CommentLayout';
import { INewComment } from './commentSchema';
import { CommentsTable } from './CommentsTable';
import { onEditCommentTypeSignature } from './EditCommentType';
import { NewComment } from './NewComment';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import {
  useCreateCommentMutation,
  useEditCommentMutation,
  useListCommentsQuery,
} from '../../../API/order/comments/CommentsApi';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import { convertToError } from '../../../API/BaseApi';
import { Show } from '../../shared/Show';
import { eCommentScope } from '../../../types/order/comment/eCommentScope';
import { useHasPermissions } from '../../accessControl/hooks/useHasPermissions';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { ISelectOption } from '../../../ui-toolkit';

export const InternalCommentsContainer: React.FC = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: comments = [], error: queryError } = useListCommentsQuery({ orderId, scope: eCommentScope.internal });
  const [createInternalComment, { error, isSuccess, isLoading, isError }] = useCreateCommentMutation();
  const [updateInternalComment] = useEditCommentMutation();

  const editPermissions = useHasPermissions([ePermissions.CommentsEdit]);

  const onSubmit = async ({ content, type }: INewComment<eCommentType>) => {
    const response = await createInternalComment({
      orderId,
      content,
      type,
      scope: eCommentScope.internal,
    });
    checkForMutationSuccess(response, 'Comment Created Successfully!');
  };

  const onEditCommentType: onEditCommentTypeSignature = async ({ commentId, commentType }) => {
    const response = await updateInternalComment({
      orderId,
      commentId,
      type: commentType,
      scope: eCommentScope.internal,
    });
    checkForMutationSuccess(response, 'Comment Updated Successfully!');
  };

  const options: ISelectOption<eCommentType>[] = useMemo(
    () =>
      internalCommentTypes.map((type) => ({
        value: type,
        label: prettyCommentsType[type],
      })),
    []
  );
  return (
    <CommentLayout pageError={queryError}>
      <Show when={editPermissions}>
        <NewComment
          onSubmit={onSubmit}
          onSubmitFetchStatus={{ fetching: isLoading, success: isSuccess, fetched: isSuccess && !isError }}
          onSubmitFetchError={convertToError(error)}
          commentTypeOptions={options}
        />
      </Show>
      <CommentsTable
        comments={comments}
        commentTypeOptions={options}
        onEditCommentType={editPermissions ? onEditCommentType : undefined}
      />
    </CommentLayout>
  );
};
